import Cookies from "js-cookie";

// Remove '/api' from the API URL to avoid double prefixing
export const BACKEND_URL = process.env.REACT_APP_API_URL.replace('/api', '')

// Create a separate constant for API endpoints to maintain clarity
export const API_BASE_URL = process.env.REACT_APP_API_URL.includes('/api') 
  ? process.env.REACT_APP_API_URL 
  : `${process.env.REACT_APP_API_URL}/api`;

export const renderValidUrl = (url) => {
  if (!url) {
    return null;
  }

  try {
    
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//')) {
      // Absolute or protocol-relative URL, normalize protocol-relative to https
      return url.startsWith('//') ? `https:${url}` : url;
    } else if (/^(www\.)/i.test(url) || /^[a-z0-9.-]+\.[a-z]{2,}(\/|$)/i.test(url)) {
      // Bare domain (with or without www.) treat as external; ensure https prefix
      return `https://${url.replace(/^https?:\/\//i, '')}`;
    } else if (url.startsWith('/uploads/')) {
      // Media uploads, append backend URL
      return `${BACKEND_URL}${url}`;
    } else if (url.startsWith('/api/')) {
      // Already has /api prefix, ensure no duplication
      return BACKEND_URL + url;
    } else if (url.startsWith('/')) {
      // URL starts with slash but not /api/ or /uploads/
      return `${BACKEND_URL}${url}`;
    } else {
      // Relative URL without leading slash
      return `${BACKEND_URL}/${url}`;
    }
  } catch (error) {
    return url; // Return original in case of error
  }
};

export const optimizeImageUrl = (
  url,
  {
    width,
    height,
    quality = "auto",
    format = "auto",
    crop = "fill",
  } = {},
) => {
  const resolvedUrl = renderValidUrl(url);

  if (!resolvedUrl || typeof resolvedUrl !== "string") {
    return resolvedUrl;
  }

  if (
    !resolvedUrl.includes("res.cloudinary.com") ||
    !resolvedUrl.includes("/upload/")
  ) {
    return resolvedUrl;
  }

  if (/\.svg(\?|$)/i.test(resolvedUrl)) {
    return resolvedUrl;
  }

  const transformations = [];

  if (format) {
    transformations.push(`f_${format}`);
  }

  if (quality) {
    transformations.push(`q_${quality}`);
  }

  if (width) {
    transformations.push(`w_${width}`);
  }

  if (height) {
    transformations.push(`h_${height}`);
  }

  if (width || height) {
    transformations.push(`c_${crop}`);
  }

  if (!transformations.length) {
    return resolvedUrl;
  }

  return resolvedUrl.replace(
    "/upload/",
    `/upload/${transformations.join(",")}/`,
  );
};

// Determine if a URL should be treated as external (opens in new tab)
export const isExternalUrl = (url = "") => {
  if (!url) return false;
  if (url.startsWith('/')) return false; // internal absolute path
  if (/^(https?:)?\/\//i.test(url)) return true; // protocol / protocol-relative
  if (/^(www\.)/i.test(url)) return true; // www.
  if (/^[a-z0-9.-]+\.[a-z]{2,}(\/|$)/i.test(url)) return true; // bare domain.tld
  return false;
};

// Normalize an external URL ensuring https scheme
export const normalizeExternalUrl = (url = "") => {
  if (!url) return '#';
  if (!isExternalUrl(url)) return url; // return as-is for internal; caller decides
  if (/^(https?:)?\/\//i.test(url)) return url.replace(/^\/\//, 'https://');
  return `https://${url.replace(/^https?:\/\//i, '')}`;
};

// Helper function for API requests to avoid the double "/api" issue
export const getApiUrl = (endpoint) => {
  // Normalize: remove any leading slashes
  const trimmed = String(endpoint).replace(/^\/+/, '');
  // If starts with 'api/', remove that prefix to avoid double /api
  const withoutApi = trimmed.startsWith('api/') ? trimmed.slice(4) : trimmed;
  return `${API_BASE_URL.replace(/\/api\/?$/, '')}/api/${withoutApi}`;
};

const expirationTimeInMinutes = 60;
export const expirationDate = new Date(new Date().getTime() + (expirationTimeInMinutes * 60 * 1000));

export const setCookieWithExpiry = (name, value, hours = 1) => {
  const date = new Date();
  date.setTime(date.getTime() + (hours * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/`;
};

export const getTokenFromCookie = () => {
  const token = Cookies.get('token');
  return token;
};

/**
 * Checks if the authentication token is valid (exists and not expired)
 * @returns {boolean} True if token is valid, false otherwise
 */
export const isTokenValid = () => {
  const token = getTokenFromCookie();
  
  if (!token) {
    return false;
  }
  
  // JWT tokens have three parts separated by dots
  const parts = token.split('.');
  if (parts.length !== 3) {
    return false;
  }
  
  try {
    // The payload is the second part, base64 encoded
    const payload = JSON.parse(atob(parts[1]));
    
    // Check if token has expiry and if it's expired
    if (payload.exp) {
      const currentTime = Math.floor(Date.now() / 1000); // Convert to seconds
      return payload.exp > currentTime;
    }
    
    // If no expiry in the token, check if it was created in the last hour
    if (payload.iat) {
      const currentTime = Math.floor(Date.now() / 1000);
      return (currentTime - payload.iat) < 3600; // 1 hour in seconds
    }
    
    return false;
  } catch (error) {
    return false;
  }
};

/**
 * Redirects to login page if user is not authenticated
 * @param {object} navigate - React Router navigate function
 * @returns {boolean} True if redirected, false if authenticated
 */
export const redirectIfNotAuthenticated = (navigate) => {
  if (!isTokenValid()) {
    navigate('/login', { replace: true });
    return true;
  }
  return false;
};

export const filterRegionsByCourseTitle = (courseObject, bannerTitle) => {
    // Get the region names where at least one course has the specified title
    const filteredRegions = Object.keys(courseObject || {}).filter((region) =>
      courseObject[region].courses.some((course) => course.title === bannerTitle)
    );

    return filteredRegions;
  }

export const downloadPDF = async (url, linkTitle='brochure.pdf') => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Network response was not ok");
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = linkTitle;
    document.body.appendChild(link);
    link.click();
    
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Failed to fetch file as blob, falling back to direct link", error);
    const link = document.createElement('a');
    link.href = url;
    link.download = linkTitle;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

// Helper function to convert a Date to EST timezone string ("YYYY-MM-DD")
export const getDateInESTString = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) {
    return null;
  }

  try {
    // Create a new date by explicitly working with UTC values and EST/EDT offset
    // EST is UTC-5, EDT is UTC-4
    // This approach is more reliable than using toLocaleString
    
    // First, clone the date to avoid modifying the original
    const clonedDate = new Date(date.getTime());
    
    // Get the date string in ISO format (YYYY-MM-DD) directly from the selected date
    // This sends the date exactly as selected by the user without timezone adjustments
    // The backend should handle timezone-specific logic
    const year = clonedDate.getFullYear();
    const month = String(clonedDate.getMonth() + 1).padStart(2, '0');
    const day = String(clonedDate.getDate()).padStart(2, '0');
    
    const dateString = `${year}-${month}-${day}`;
   
    return dateString;
  } catch (error) {
    // Fallback: format the original date if conversion fails
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  }
};

const allowablePagePaths = {
  Home: "/",
  About: "/about",
  FAQ: "/resources/faq",
  Blog: "/resources/blog",
};

export function showPromoOnThisPage(pagePath, data) {
  const { showOnPages, showOnAllPages } = data;

  // if showOnAllPages is true show on allowable pages
  if (showOnAllPages) {
    for (let path in allowablePagePaths){
      if (allowablePagePaths[path] === pagePath) return true
    }
    return false
  }

  // If showOnPages is an array and the current pagePath is included in the showOnPages array, return true
  if (showOnPages && Array.isArray(showOnPages)) {
    for (let path in allowablePagePaths){
      if (allowablePagePaths[path] === pagePath && showOnPages.includes(path)) return true
    }
    return false;
  }

  // If none of the conditions above match, return false
  return false;
}

export const PARTNERDROPDOWN = [
  "Select",
  "Become a Mentor/Educator/Instructor",
  "Become a UX/UI Volunteer",
  "Become a Software Developer Volunteer",
  "Sponsorship",
  "Work with us",
  "Other",
]

export const WORKDROPDOWN = [
  "Select",
  "Become a Mentor/Educator/Instructor",
  "Become a UX/UI Volunteer",
  "Become a Software Developer volunteer", 
  "Become a BA"
]

export const PASSWORDREGEX = new RegExp(
  /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,20}$/
);

export const PASSWORDLIST = ['At least 8 characters', 'Numbers and special characters','Uppercase and lowercase'];

export const CALENDLY_URL = "https://meet.brevo.com/theskillgarage-intro-call"
